import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";

const keyFor = (email, kind) => `dplus_${kind}_${(email || "guest").toLowerCase()}`;

const read = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};
const write = (key, value) => localStorage.setItem(key, JSON.stringify(value));

export const useUserData = () => {
  const { user } = useAuth();
  const email = user?.email;

  // Orders & designs — empty until backend is live or checkout adds entries
  const [orders, setOrders] = useState(() => read(keyFor(email, "orders"), []));
  const [designs, setDesigns] = useState(() => read(keyFor(email, "designs"), []));
  const [addresses, setAddresses] = useState(() => read(keyFor(email, "addresses"), []));

  // Re-read whenever the signed-in user changes
  useEffect(() => {
    setOrders(read(keyFor(email, "orders"), []));
    setDesigns(read(keyFor(email, "designs"), []));
    setAddresses(read(keyFor(email, "addresses"), []));
  }, [email]);

  useEffect(() => { if (email) write(keyFor(email, "orders"), orders); }, [email, orders]);
  useEffect(() => { if (email) write(keyFor(email, "designs"), designs); }, [email, designs]);
  useEffect(() => { if (email) write(keyFor(email, "addresses"), addresses); }, [email, addresses]);

  const addAddress = useCallback((addr) => {
    setAddresses((prev) => {
      const id = addr.id || `addr-${Date.now()}`;
      let next = [...prev, { ...addr, id }];
      if (addr.isDefault) next = next.map((a) => ({ ...a, isDefault: a.id === id }));
      else if (next.length === 1) next = next.map((a) => ({ ...a, isDefault: true }));
      return next;
    });
  }, []);

  const updateAddress = useCallback((id, patch) => {
    setAddresses((prev) => {
      let next = prev.map((a) => (a.id === id ? { ...a, ...patch } : a));
      if (patch.isDefault) next = next.map((a) => ({ ...a, isDefault: a.id === id }));
      return next;
    });
  }, []);

  const removeAddress = useCallback((id) => {
    setAddresses((prev) => {
      const next = prev.filter((a) => a.id !== id);
      if (next.length && !next.some((a) => a.isDefault)) next[0].isDefault = true;
      return next;
    });
  }, []);

  const makeDefault = useCallback((id) => {
    setAddresses((prev) => prev.map((a) => ({ ...a, isDefault: a.id === id })));
  }, []);

  return { orders, designs, addresses, addAddress, updateAddress, removeAddress, makeDefault };
};
